import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "./ManageUsers.css";
import API_BASE_URL from "../config";

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    roles: [],
  });
  const [search, setSearch] = useState(""); // ✅ Search state
  const navigate = useNavigate();

  // 🔐 Authentication check
  useEffect(() => {
    const token = sessionStorage.getItem("token"); // or sessionStorage
    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
    }
  }, [navigate]);

  // ✅ Fetch users & roles
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/users`)
      .then((res) => res.json())
      .then((data) => setUsers(data))
      .catch((err) => console.error("Error fetching users:", err));

    fetch(`${API_BASE_URL}/api/roles/all`)
      .then((res) => res.json())
      .then((data) => setRoles(data.map((r) => r.name)))
      .catch((err) => console.error("Error fetching roles:", err));
  }, []);

  // ✅ Format role names for UI (remove ROLE_)
  const formatRole = (role) => role.replace("ROLE_", "");

  // ✅ Start editing
  const handleEdit = (user) => {
    setEditingUser(user.id);
    setFormData({
      name: user.name,
      email: user.email,
      password: "",
      roles: user.roles,
    });
  };

  // ✅ Cancel editing
  const handleCancel = () => {
    setEditingUser(null);
    setFormData({ name: "", email: "", password: "", roles: [] });
  };

  // ✅ Update input
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ✅ Toggle role
  const toggleRole = (role) => {
    setFormData((prev) => {
      const alreadySelected = prev.roles.includes(role);
      return {
        ...prev,
        roles: alreadySelected
          ? prev.roles.filter((r) => r !== role)
          : [...prev.roles, role],
      };
    });
  };

  // ✅ Save updated user
  const handleSave = (id) => {
    const payload = {
      ...formData,
      roles: formData.roles.map((r) =>
        r.startsWith("ROLE_") ? r : `ROLE_${r.toUpperCase()}`
      ),
    };

    fetch(`${API_BASE_URL}/api/users/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to update user");
        return res.json();
      })
      .then((updatedUser) => {
        setUsers(users.map((u) => (u.id === id ? updatedUser : u)));
        handleCancel();
      })
      .catch((err) => alert("Error updating user: " + err.message));
  };

  // ✅ Delete user
  const handleDelete = (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    fetch(`${API_BASE_URL}/api/users/${id}`, { method: "DELETE" })
      .then((res) => {
        if (res.ok) {
          setUsers(users.filter((u) => u.id !== id));
        } else {
          throw new Error("Delete failed");
        }
      })
      .catch((err) => alert("Error deleting user: " + err.message));
  };

  // ✅ Filtered users (search by name, email, roles)
  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      u.roles.some((r) =>
        formatRole(r).toLowerCase().includes(search.toLowerCase())
      )
  );

  return (
    <div className="manage-users-container">
      <h2>Manage Users</h2>

      {/* ✅ Search Box */}
      <input
        type="text"
        placeholder="Search by name, email, or role..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="search-box"
      />

      <table className="users-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Roles</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
              <tr key={user.id}>
                <td>
                  {editingUser === user.id ? (
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                  ) : (
                    user.name
                  )}
                </td>
                <td>
                  {editingUser === user.id ? (
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  ) : (
                    user.email
                  )}
                </td>
                <td>
                  {editingUser === user.id ? (
                    <div className="roles-edit">
                      {roles.map((role, i) => (
                        <label key={i}>
                          <input
                            type="checkbox"
                            checked={formData.roles.includes(role)}
                            onChange={() => toggleRole(role)}
                          />
                          {formatRole(role)}
                        </label>
                      ))}
                    </div>
                  ) : (
                    user.roles.map((r) => formatRole(r)).join(", ")
                  )}
                </td>
                <td>
                  {editingUser === user.id ? (
                    <>
                      <button
                        className="btn save-btn"
                        onClick={() => handleSave(user.id)}
                      >
                        Save
                      </button>
                      <button className="btn cancel-btn" onClick={handleCancel}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn edit-btn"
                        onClick={() => handleEdit(user)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn delete-btn"
                        onClick={() => handleDelete(user.id)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4">No users found.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ManageUsers;
